import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { InventoryChangeDetail, RunSummary } from "encamp-shared/src/hmbp/wadmRunSummary";

/**
 * Renders the WADM run summary stored on `Job.output` for `PROCESS_AMAZON_WADM`
 * jobs. Two accordions:
 *   - "Run Summary" — top-line counts, CUPA breakdown, chip lists for
 *     unmatched profile codes / missing chemicals / unmatched site codes /
 *     facilities with a pre-existing open UPDATE that this run skipped.
 *   - "Inventory Changes" — per-row before/after table.
 *
 * The caller is responsible for gating render on
 * `task === ProcessAmazonWadm && isWadmRunSummary(output)` so this component
 * receives a fully-typed summary.
 */
export function WadmRunSummarySection({ summary }: { summary: RunSummary }) {
  return (
    <Stack spacing={2}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            WADM Run Summary
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RunSummaryBody summary={summary} />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Inventory Changes ({summary.inventoryChanges.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <InventoryChangesTable changes={summary.inventoryChanges} />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

function RunSummaryBody({ summary }: { summary: RunSummary }) {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={4} flexWrap="wrap">
        <CounterStat
          label="Facilities matched"
          value={summary.facilitiesMatched}
          secondary={`of ${summary.facilitiesAttempted.toLocaleString()} attempted`}
        />
        <CounterStat label="Items updated" value={summary.itemsUpdated} />
        <CounterStat label="Reports created" value={summary.reportsCreated} />
      </Stack>

      {summary.cupaBreakdown.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary">
            CUPA breakdown
          </Typography>
          <TableContainer sx={{ mt: 0.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CUPA</TableCell>
                  <TableCell>County</TableCell>
                  <TableCell align="right">Reports</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.cupaBreakdown.map((c, i) => (
                  <TableRow key={`${c.lepcName ?? "null"}::${c.county ?? "null"}::${i}`}>
                    <TableCell>{c.lepcName ?? "—"}</TableCell>
                    <TableCell>{c.county ?? "—"}</TableCell>
                    <TableCell align="right">{c.reportCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <ChipList
        label="Unmatched profile codes"
        color="error"
        values={summary.unmatchedProfileCodes}
      />
      <ChipList label="Missing chemicals" color="error" values={summary.missingChemicals} />
      <ChipList label="Unmatched site codes" color="error" values={summary.unmatchedSiteCodes} />
      <ChipList
        label="Facilities with an open UPDATE (skipped this run)"
        color="info"
        values={summary.facilityIdsWithExistingOpenUpdate}
      />
    </Stack>
  );
}

function CounterStat({
  label,
  value,
  secondary,
}: {
  label: string;
  value: number;
  secondary?: string;
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight="medium">
        {value.toLocaleString()}
      </Typography>
      {secondary !== undefined && (
        <Typography variant="caption" color="text.secondary">
          {secondary}
        </Typography>
      )}
    </Box>
  );
}

function ChipList({
  label,
  color,
  values,
}: {
  label: string;
  color: "error" | "info";
  values: string[];
}) {
  if (values.length === 0) return null;
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label} ({values.length})
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
        {values.map((v) => (
          <Chip key={v} label={v} size="small" color={color} />
        ))}
      </Box>
    </Box>
  );
}

function InventoryChangesTable({ changes }: { changes: InventoryChangeDetail[] }) {
  if (changes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No inventory rows were created or updated this run.
      </Typography>
    );
  }
  return (
    // Cap the height so very large runs (thousands of rows) scroll within a
    // bounded region instead of expanding the page inline. Header stays
    // sticky as the user scrolls.
    <TableContainer sx={{ maxHeight: 480 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Facility</TableCell>
            <TableCell>Chemical</TableCell>
            <TableCell>Kind</TableCell>
            <TableCell align="right">Prior Max</TableCell>
            <TableCell align="right">New Max</TableCell>
            <TableCell align="right">Max Code</TableCell>
            <TableCell align="right">Prior Avg</TableCell>
            <TableCell align="right">New Avg</TableCell>
            <TableCell align="right">Avg Code</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {changes.map((c) => (
            <TableRow key={`${c.facilityId}::${c.chemicalId}`}>
              <TableCell>{c.facilityName ?? c.facilityId}</TableCell>
              <TableCell>{c.chemicalName ?? c.chemicalId}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={c.kind}
                  color={
                    c.kind === "created" ? "success" : c.kind === "updated" ? "info" : "default"
                  }
                />
              </TableCell>
              <TableCell align="right">{formatAmount(c.priorMaxAmount)}</TableCell>
              <TableCell align="right">{formatAmount(c.newMaxAmount)}</TableCell>
              <TableCell align="right">
                {formatCodeTransition(c.priorMaxAmountCode, c.newMaxAmountCode)}
              </TableCell>
              <TableCell align="right">{formatAmount(c.priorAverageAmount)}</TableCell>
              <TableCell align="right">{formatAmount(c.newAverageAmount)}</TableCell>
              <TableCell align="right">
                {formatCodeTransition(c.priorAverageAmountCode, c.newAverageAmountCode)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function formatAmount(n: number | null): string {
  if (n === null) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatCodeTransition(prior: number | null, next: number | null): string {
  if (prior === null && next === null) return "—";
  if (prior === null) return String(next);
  if (prior === next) return String(next);
  return `${prior} → ${next ?? "—"}`;
}
